import { useParams } from 'react-router-dom'
import { useAuth } from './../../auth/hooks/useAuth'
import { IChannel, IChannelMessageResponse, IChannelUser } from './../../../common/types/channels.types'
import { useState } from 'react'
import { useChannelsApi } from './../../../hooks/use-channels-api/useChannelsApi'
import { BackendPaginatedResponse, } from 'common/types'
import { useUserApi } from 'hooks/use-user-api/useUserApi'
import { AnalyticUserType, useAnalytics } from 'hooks/use-analytics/useAnalytics'

export const useChannel = () => {
  const { channelId } = useParams<{ channelId: string }>()
  const { getChannelMessages, createChannelMessage, fetchChannel, fetchChannelUsers, getChannelUser, deleteChannelMember } = useChannelsApi()
  const { backendUser, backendPartner } = useAuth()
  const { fetchAnyUser } = useUserApi()

  const [messages, setMessages] = useState<IChannelMessageResponse[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [isLastPage, setIsLastPage] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [channel, setChannel] = useState<IChannel>()
  const [channelUsers, setChannelUsers] = useState<IChannelUser[]>([])
  const [TotalChannelUsers, setTotalChannelUsers] = useState<any>(0)
  const [focusMessage, setFocusMessage] = useState('')
  const [channelUser, setChannelUser] = useState<boolean>(false)
  const { handleTracker } = useAnalytics()

  const getMessages = async (page = 1, pageSize = 5, refresh = false) => {
    if (!isLastPage || refresh) {
      let fetchPage = page ?? currentPage

      if (refresh) {
        fetchPage = 1
      }

      setIsLoading(true)
      const response = await getChannelMessages(backendUser?.id, channelId, fetchPage, pageSize)
      if (response) {
        const messages = response?.data
        const newChannelMessages = await Promise.all(messages.map(async (channelMessage) => {
          const sender = await fetchAnyUser(channelMessage?.senderId) // TODO we shouldn't be fetching users for every message. sender info should be included in message from server
          return {
            ...channelMessage,
            sender
          }
        }))


        if (newChannelMessages?.length) {
          newChannelMessages?.reverse()

          if (refresh) {
            setMessages(newChannelMessages)
          } else {
            setMessages(prev => [...prev, ...newChannelMessages])
          }

          setFocusMessage(newChannelMessages[newChannelMessages.length - 1]?.id)
        } else {
          setMessages([])
        }

        newChannelMessages?.length < 5 && setIsLastPage(true)
        setCurrentPage(window.parseInt(response.page))
      }
      setIsLoading(false)
    }
  }

  const createMessage = async (message: string) => {
    const response = await createChannelMessage(backendUser?.id, {
      channelId,
      content: {
        message
      }
    })

    const logData = {
      'Channel ID': channel?.id,
      'Channel Name': channel?.name,
      'Channel Type': channel?.type,
      'Sender ID': backendUser?.id,
      'Message ID': response.id,
      'Sender role': backendUser?.partnerId ? 'Partner' : backendUser?.role,
      'Sender Email': backendUser?.username,
      'Partner ID': backendPartner?.id,
      'Company Name': backendPartner?.name
    }

    await handleTracker(AnalyticUserType.ALL, 'Channel Message Sent', logData, 'mixpanel', null, backendUser)

    if (response) {
      setMessages([...messages, { ...response, senderAvatar: backendUser?.image, sender: backendUser }])
      setFocusMessage(response.id)
    }
  }

  const getChannel = async () => {
    setIsLoading(true)
    const response = await fetchChannel(channelId)

    if (response) {
      setChannel(response)
    }
    setIsLoading(false)
    return response
  }

  const getChannelUsers = async (page: number, pageSize: number) => {
    const response: BackendPaginatedResponse<IChannelUser> = await fetchChannelUsers(channelId, page, pageSize)
    if (response) {
      setChannelUsers([...channelUsers, ...response.data])
      setTotalChannelUsers(response?.totalCount)
    }
  }

  const removeChannelMember = async (userId: string, channelUserId: string) => {
    const response = await deleteChannelMember(userId, channelUserId)
    return response
  }

  const getChannelUserType = async (channelId: string, userId: string) => {
    const response = await getChannelUser(channelId, userId)
    if (response) {
      setChannelUser(response?.isAdmin)
    }

    return response
  }

  return {
    messages,
    getMessages,
    isLastPage,
    isLoading,
    createMessage,
    getChannel,
    getChannelUser,
    getChannelUsers,
    channel,
    channelUsers,
    focusMessage,
    removeChannelMember,
    TotalChannelUsers,
    getChannelUserType,
    channelUser,
    setChannelUsers
  } as const
}
